import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LoginPage from "./pages/LoginPage";
import BookingPage from "./pages/BookingPage";
import AdminDashboard from "./pages/AdminDashboard";
import PatientDashboard from "./pages/PatientDashboard";
import SettingsPage from "./pages/SettingsPage";
import { Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth context helper
export const getAuthToken = () => localStorage.getItem("adminToken");
export const setAuthToken = (token) => localStorage.setItem("adminToken", token);
export const clearAuthToken = () => localStorage.removeItem("adminToken");

export const apiClient = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = getAuthToken();
  if (!token) {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!getAuthToken());

  useEffect(() => {
    const checkAuth = async () => {
      const token = getAuthToken();
      if (token) {
        try {
          await apiClient.get("/auth/me");
          setIsAuthenticated(true);
        } catch {
          clearAuthToken();
          setIsAuthenticated(false);
        }
      }
    };
    checkAuth();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          {/* Public booking page at root */}
          <Route path="/" element={<BookingPage />} />
          
          {/* Admin login page */}
          <Route 
            path="/admin" 
            element={
              isAuthenticated ? 
                <Navigate to="/admin/dashboard" replace /> : 
                <LoginPage setIsAuthenticated={setIsAuthenticated} />
            } 
          />
          
          {/* Admin dashboard */}
          <Route 
            path="/admin/dashboard" 
            element={
              <ProtectedRoute>
                <AdminDashboard setIsAuthenticated={setIsAuthenticated} />
              </ProtectedRoute>
            } 
          />
          
          {/* Admin settings */}
          <Route 
            path="/admin/settings" 
            element={
              <ProtectedRoute>
                <SettingsPage setIsAuthenticated={setIsAuthenticated} />
              </ProtectedRoute>
            } 
          />
          
          {/* Patient dashboard via magic link */}
          <Route path="/patient" element={<PatientDashboard />} />
          
          {/* Legacy route redirect */}
          <Route path="/book" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
